/**
 * Tab block — URL sync + selector visibility via tab-hidden-{blockId}.
 * Not a Web Component: mutates external DOM by selectors.
 */
(function () {
  if (window.__mammotionTabJsBound) return;
  window.__mammotionTabJsBound = true;
  /**
   * @param {string} paramName
   * @param {string} defaultKey
   * @returns {string}
   */
  function getTabFromUrl(paramName, defaultKey) {
    const url = new URL(window.location.href);
    return url.searchParams.get(paramName) || defaultKey || '';
  }

  /**
   * Resolve Shopify section/block IDs whose environment hash segment changes
   * across dev / staging / production.
   *
   * Supported formats:
   * - #shopify-section-template--{hash}__{suffix}
   * - #shopify-block-{hash}__{suffix}
   *
   * Matching ignores the variable hash and keys off the stable suffix only.
   *
   * @param {string} selector
   * @returns {Element | null}
   */
  function findElementByShopifyIdSelector(selector) {
    const cleaned = selector.replace(/^#/, '').trim();
    const suffixParts = cleaned.split('__');
    if (suffixParts.length < 2) return null;

    const suffix = suffixParts.slice(1).join('__');
    if (!suffix) return null;

    if (cleaned.startsWith('shopify-section-template--')) {
      return document.querySelector(
        `[id^="shopify-section-template--"][id$="__${CSS.escape(suffix)}"]`
      );
    }

    if (cleaned.startsWith('shopify-block-')) {
      return document.querySelector(`[id^="shopify-block-"][id$="__${CSS.escape(suffix)}"]`);
    }

    return null;
  }

  /**
   * @param {string} selector
   * @returns {Element[]}
   */
  function resolveTabSelectorElements(selector) {
    if (
      selector.startsWith('#shopify-section-template') ||
      selector.startsWith('#shopify-block-')
    ) {
      const element = findElementByShopifyIdSelector(selector);
      return element ? [element] : [];
    }

    return Array.from(document.querySelectorAll(selector));
  }

  /**
   * @param {string} blockId
   */
  function ensureHiddenClassStyle(blockId) {
    const styleId = `tab-hidden-style-${blockId}`;
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `.tab-hidden-${blockId} { display: none !important; }`;
    document.head.appendChild(style);
  }

  /**
   * @param {string} blockId
   */
  function updateSlider(blockId) {
    const container = document.querySelector(`#tab-list-${blockId}`);
    if (!container) return;

    const activeItem = container.querySelector('.tab-item.active');
    if (activeItem) {
      container.style.setProperty('--tab-width', `${activeItem.offsetWidth}px`);
      container.style.setProperty('--tab-left', `${activeItem.offsetLeft}px`);
    } else {
      container.style.setProperty('--tab-width', '0px');
    }
  }

  /**
   * @param {string} blockId
   * @param {string} currentTab
   */
  function updateTabActiveState(blockId, currentTab) {
    const root = document.querySelector(`[data-tab-block="${blockId}"]`);
    if (!root) return;

    root.querySelectorAll('.tab-item').forEach((tab) => {
      const isActive = tab.getAttribute('data-select-page') === currentTab;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    updateSlider(blockId);
  }

  /**
   * @param {string} blockId
   * @param {Element} tabItem
   * @param {'show' | 'hide'} action
   */
  function updateTabSelectors(blockId, tabItem, action) {
    if (!tabItem) return;

    const hiddenClass = `tab-hidden-${blockId}`;
    const selectorsStr = tabItem.getAttribute('data-selectors');
    if (!selectorsStr || !selectorsStr.trim()) return;

    const selectors = selectorsStr
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean);

    selectors.forEach((selector) => {
      const elements = resolveTabSelectorElements(selector);

      if (!elements.length) {
        console.warn(`[tab] No elements found for selector: ${selector}`);
        return;
      }

      elements.forEach((element) => {
        if (action === 'hide') {
          element.classList.add(hiddenClass);
        } else {
          element.classList.remove(hiddenClass);
        }
      });
    });
  }

  /**
   * @param {string} blockId
   * @param {Element} tabElement
   * @param {ScrollBehavior} [behavior]
   */
  function scrollTabIntoView(blockId, tabElement, behavior = 'smooth') {
    const tabList = document.querySelector(`#tab-list-${blockId}`);
    if (!tabList || !tabElement) return;

    const visibleWidth = tabList.clientWidth;
    if (visibleWidth <= 0) return;

    const targetLeft = tabElement.offsetLeft - (visibleWidth - tabElement.offsetWidth) / 2;
    const maxScrollLeft = Math.max(tabList.scrollWidth - tabList.clientWidth, 0);
    const nextScrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);

    tabList.scrollTo({
      left: nextScrollLeft,
      behavior,
    });
  }

  /**
   * @param {string} blockId
   */
  function updateTabVisibility(blockId) {
    const root = document.querySelector(`[data-tab-block="${blockId}"]`);
    if (!root) return;

    const paramName = root.dataset.searchParam || 'tab';
    const defaultKey = root.dataset.defaultTabKey || '';
    const allTabs = root.querySelectorAll('.tab-item');
    if (!allTabs.length) return;

    ensureHiddenClassStyle(blockId);

    const currentTab = getTabFromUrl(paramName, defaultKey);
    let showTab = Array.from(allTabs).find((tab) => tab.getAttribute('data-select-page') === currentTab);

    if (!showTab) {
      showTab = allTabs[0];
      if (currentTab) {
        console.warn(
          `[tab] Tab key "${currentTab}" not found, falling back to "${showTab.getAttribute('data-select-page')}"`
        );
      }
    }

    const activeKey = showTab.getAttribute('data-select-page');
    updateTabActiveState(blockId, activeKey);

    allTabs.forEach((tab) => {
      if (tab !== showTab) {
        updateTabSelectors(blockId, tab, 'hide');
      }
    });
    updateTabSelectors(blockId, showTab, 'show');
    scrollTabIntoView(blockId, showTab, 'auto');
  }

  /**
   * @param {HTMLElement} root
   */
  function initTabBlock(root) {
    const blockId = root.dataset.tabBlock;
    if (!blockId || root.dataset.tabInitialized === 'true') return;
    root.dataset.tabInitialized = 'true';

    const paramName = root.dataset.searchParam || 'tab';
    const tabList = root.querySelector(`#tab-list-${blockId}`);

    updateTabVisibility(blockId);
    requestAnimationFrame(() => updateSlider(blockId));

    const onActivate = (target) => {
      const tabItem = target.closest('.tab-item');
      if (!tabItem || !tabList || !tabList.contains(tabItem)) return;

      const selected = tabItem.getAttribute('data-select-page');
      const tabs = tabList.querySelectorAll('.tab-item');

      tabs.forEach((tab) => {
        if (tab !== tabItem) {
          updateTabSelectors(blockId, tab, 'hide');
        }
      });
      updateTabSelectors(blockId, tabItem, 'show');
      updateTabActiveState(blockId, selected);
      scrollTabIntoView(blockId, tabItem);

      const url = new URL(window.location.href);
      url.searchParams.set(paramName, selected);
      window.history.pushState({ tab: selected, tabBlockId: blockId }, '', url.pathname + url.search);
    };

    tabList?.addEventListener('click', (event) => {
      onActivate(event.target);
    });

    tabList?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onActivate(event.target);
    });

    const onUrlChange = () => updateTabVisibility(blockId);
    window.addEventListener('popstate', onUrlChange);
    window.addEventListener('hashchange', onUrlChange);
    window.addEventListener('resize', () => updateSlider(blockId));
  }

  function initAllTabs(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll('[data-tab-block]').forEach((root) => {
      root.dataset.tabInitialized = 'false';
      initTabBlock(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initAllTabs());
  document.addEventListener('shopify:section:load', (event) => initAllTabs(event.target));
})();
